<template>
  <v-dialog v-model="dialog" max-width="90%" persistent>
    <v-snackbar v-model="snackbar.model" top color="warning">
      {{ snackbar.detail }}
    </v-snackbar>
    <v-card>
      <v-card-title>Upload your bank transactions</v-card-title>
      <v-card-text>
        <v-container>
          <v-row>
            <v-col>
              <v-select :items="bankAccounts" v-model="bankAccount" label="Bank account"></v-select>
            </v-col>
            <v-col>
              <v-file-input v-model="fileObj" v-if="bankAccount !== null"
                label="Click here to select file" :disabled="loading || bankAccount === null" :loading="loading"
                prepend-icon="attach_file" @change="loadFile"
                accept="text/csv" :clearable="false" outlined>
              </v-file-input>
            </v-col>
          </v-row>
          <v-row>
            <v-col>
              <v-data-table v-if="csvHeaders && !loading" disable-sort
                :headers="csvHeaders" :items="csvRows" class="elevation-1" :items-per-page="10"
                :footer-props="{disableItemsPerPage: true}" hide-default-header>
                <template v-slot:header="{ props: { headers } }">
                  <thead>
                    <tr>
                      <th v-for="th in headers" :key="th.value">
                        {{ th.text }}
                        <v-checkbox class="small" dense v-model="th.isDesc"
                          label="DESCRIPTION" @click.native="onDescChange(th)"
                          v-if="th.text !== 'Date' && th.text !== 'Amount' && th.value !== 'is_dup'">
                        </v-checkbox>
                      </th>
                    </tr>
                  </thead>
                </template>
                <template v-slot:item.is_dup="{ item }">
                  <v-icon v-if="item.is_dup" color="error">clear</v-icon>
                  <v-icon v-else color="success">check</v-icon>
                </template>
              </v-data-table>
            </v-col>
          </v-row>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-container>
          <v-row>
            <v-col cols="12" v-if="uploading">
              Uploading... <v-progress-linear :value="uploadProgress"></v-progress-linear>
            </v-col>
            <v-col cols="12" offset-md="4" md="4">
              <v-btn block @click="upload" :disabled="uploadDisabled">Upload</v-btn>
            </v-col>
            <v-col cols="12" md="4">
              <v-btn block @click="close">Cancel</v-btn>
            </v-col>
          </v-row>
        </v-container>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import sha1 from 'sha1'
// import { Api } from '@/services/Api'
import { Vue, Component, Watch } from 'vue-property-decorator'
import { namespace, Getter } from 'vuex-class'
import VuePapaParse from 'vue-papa-parse'

Vue.use(VuePapaParse)
const SubModule = namespace('transaction')

@Component
export default class StatementUploader extends Vue {
  @SubModule.Getter fields
  @SubModule.Action setFieldItems
  @Getter entity

  dialog = false
  loading = false
  fileObj = null
  csvReader = new FileReader()
  csvHeaders = null
  csvRows = []
  snackbar = {
    model: false,
    detail: ''
  }
  uploading = false
  unloaded = null
  uploadProgress = 0
  bankAccount = null
  bankAccounts = []
  hasOverlapTrans = false

  mounted () {
    this.csvReader.onload = this.onFileLoad
  }

  setBankAccounts (values) {
    this.bankAccounts = values
  }

  get descHeader () {
    if (this.csvHeaders === null) {
      return null
    }
    const descHeaders = this.csvHeaders.filter(th => th.isDesc)
    if (descHeaders.length === 0) {
      return null
    }
    return descHeaders[0]
  }

  get uploadDisabled () {
    return !this.bankAccount || !this.descHeader || this.uploading
  }

  open () {
    this.dialog = true
  }

  close () {
    this.fileObj = null
    this.csvHeaders = null
    this.csvRows = []
    this.unloaded = null
    this.bankAccount = null
    this.bankAccounts = []
    this.dialog = false
  }

  loadFile () {
    this.loading = true
    this.csvHeaders = null
    this.csvRows = []
    this.csvReader.readAsText(this.fileObj)
  }

  async upload () {
    const descTh = this.descHeader
    if (descTh === null) {
      this.snackbar.detail = 'No description field selected!'
      this.snackbar.model = true
      return
    }
    this.uploading = true
    this.uploadProgress = 0
    this.unloaded = this.csvRows.length
    this.csvRows.map(row => {
      if (row.is_dup) {
        this.unloaded--
        return
      }
      const data = { id: null, items: [] }
      data.items = [
        { name: 'entity', value: this.entity.id },
        { name: 'transtime', value: row.date },
        { name: 'amount', value: Math.abs(row.amount) },
        { name: 'trans_type', value: row.amount > 0 ? 'D' : 'W' },
        { name: 'description', value: row[descTh.value] },
        { name: 'is_uploaded', value: true },
        { name: 'bank_account', value: this.bankAccount },
        { name: 'trans_source', value: 'B' },
        { name: 'trans_hash', value: row.trans_hash }
      ]
      this.setFieldItems(data).then(() => { this.unloaded-- })
    })
  }

  @Watch('unloaded')
  onUnloaded (val) {
    this.uploadProgress = parseInt((this.csvRows.length - this.unloaded) * 100 / this.csvRows.length)
    if (val === 0) {
      this.uploading = false
      this.$emit('statement-uploaded')
      this.$emit('show-message', {
        message: 'Bank statement uploade!'
      })
      this.unloaded = null
      this.close()
    }
  }

  onDescChange (th) {
    if (th.isDesc) {
      this.csvHeaders.map(h => {
        if (h.value !== th.value) {
          h.isDesc = false
        }
      })
    }
  }

  onFileLoad (e) {
    const result = this.$papa.parse(e.target.result)
    for (let row of result.data) {
      if (this.csvHeaders === null && /^38-/.test(row[0])) {
        // kiwi bank basic
        this.csvHeaders = [
          { text: 'Date', value: 'date', isDesc: false },
          { text: 'Description', value: 'description', isDesc: false },
          { text: 'Code', value: 'code', isDesc: false },
          { text: 'Amount', value: 'amount', isDesc: false },
          { text: 'Balance', value: 'balance', isDesc: false }
        ]
      } else if (row.indexOf('Date') >= 0 && row.indexOf('Amount') >= 0) {
        this.csvHeaders = row.map(v => {
          return { text: v, value: v.replace(/\s+/, '_').toLowerCase(), isDesc: false }
        })
      } else if (this.csvHeaders !== null) {
        const nonEmptyItems = row.filter(v => !!v)
        if (nonEmptyItems.length > 0) {
          let item = {}
          this.csvHeaders.map((v, n) => {
            item[v.value] = row[n]
          })
          item.trans_hash = sha1(row.toString())
          this.csvRows.push(item)
        }
      }
    }
    /*
    const transHashes = this.csvRows.map(v => v.trans_hash)
    let hashes = []
    let noBankTrans = false
    Api().post('/cashbook/transaction/check/', { 'trans_hashes': transHashes, 'bank_account': this.bankAccount })
      .then(resp => {
        if (resp.status === 204) {
          noBankTrans = true
        } else if (resp.status === 200) {
          hashes = resp.data.hashes
        }
        this.csvRows.map(v => {
          v.is_dup = hashes.filter(h => h === v.trans_hash).length > 0
        })

        const hasDup = this.csvRows.filter(v => v.is_dup).length > 0
        if (!noBankTrans && this.csvRows.length > 0 && !hasDup) {
          this.snackbar.detail = 'Must have overlap transactions of previous import!'
          this.snackbar.model = true
        } else {
          this.hasOverlapTrans = true
        }
        this.csvHeaders.splice(0, 0, { text: 'Check duplicate?', value: 'is_dup', isDesc: false })
      })
    */
    this.loading = false
  }
}
</script>

<style lang="scss">
.small {
  .v-label { font-size: small; }
}
</style>
